/**
 * Certificate Verification Page (Public)
 * Allows anyone to verify a certificate by code
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Shield,
  CheckCircle,
  XCircle,
  Award,
  Clock,
  User,
  BookOpen,
  Calendar,
  BadgeCheck,
} from "lucide-react";
import { Button } from "@learn-me/ui";
import { useVerifyCertificate } from "@/features/certificates/hooks/useCertificates";

export default function CertificateVerifyPage() {
  const [searchParams] = useSearchParams();
  const initialCode = searchParams.get("code") || "";
  const [code, setCode] = useState(initialCode);
  const [submittedCode, setSubmittedCode] = useState(initialCode);

  const { data, isLoading, error } = useVerifyCertificate(submittedCode);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedCode(code.trim().toUpperCase());
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-xl font-bold">ARCUS Learning</h1>
              <p className="text-sm text-muted-foreground">
                Verificación de certificados
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Search Form */}
        <div className="bg-white rounded-xl shadow-sm border p-8 mb-8">
          <h2 className="text-2xl font-bold text-center mb-2">
            Verificar certificado
          </h2>
          <p className="text-center text-muted-foreground mb-6">
            Ingresa el código del certificado para verificar su autenticidad
          </p>

          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex gap-3">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ARCUS-XXXXX-XXXXX-XXXXXXXX"
                className="flex-1 rounded-md border border-input bg-background px-4 py-2 text-center font-mono tracking-wider"
              />
              <Button type="submit" disabled={isLoading || code.length < 10}>
                {isLoading ? "Verificando..." : "Verificar"}
              </Button>
            </div>
          </form>
        </div>

        {/* Results */}
        {submittedCode && !isLoading && (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {error ? (
              <div className="p-8 text-center">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-red-900">
                  Error al verificar
                </h3>
                <p className="text-red-600 mt-2">
                  No se pudo verificar el certificado. Intenta de nuevo.
                </p>
              </div>
            ) : data?.valid === false ? (
              <div className="p-8 text-center">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-red-900">
                  Certificado no válido
                </h3>
                <p className="text-red-600 mt-2">{data.message}</p>
                {data.revokedAt && (
                  <div className="mt-4 bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-red-700">
                      <strong>Revocado el:</strong>{" "}
                      {new Date(data.revokedAt).toLocaleDateString("es-ES")}
                    </p>
                    {data.revocationReason && (
                      <p className="text-sm text-red-600 mt-1">
                        <strong>Razón:</strong> {data.revocationReason}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : data?.valid ? (
              <>
                {/* Valid Certificate Header */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <CheckCircle className="h-8 w-8" />
                    <h3 className="text-2xl font-bold">Certificado válido</h3>
                  </div>
                  <p className="text-center text-green-100">
                    Este certificado es auténtico y fue emitido por ARCUS
                    Learning
                  </p>
                </div>

                {/* Certificate Details */}
                <div className="p-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Otorgado a
                          </p>
                          <p className="font-semibold text-lg">
                            {data.certificate?.recipientName}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <BookOpen className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Curso</p>
                          <p className="font-semibold">
                            {data.certificate?.courseTitle}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Fecha de emisión
                          </p>
                          <p className="font-semibold">
                            {data.certificate?.issuedAt &&
                              new Date(
                                data.certificate.issuedAt,
                              ).toLocaleDateString("es-ES", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Horas completadas
                          </p>
                          <p className="font-semibold">
                            {data.certificate?.hoursCompleted} horas
                          </p>
                        </div>
                      </div>

                      {data.certificate?.finalExamScore && (
                        <div className="flex items-start gap-3">
                          <Award className="h-5 w-5 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm text-muted-foreground">
                              Examen final
                            </p>
                            <p className="font-semibold">
                              {data.certificate.finalExamScore}%
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-start gap-3">
                        <BadgeCheck className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Identidad verificada
                          </p>
                          <p className="font-semibold flex items-center gap-2">
                            {data.certificate?.identityVerified ? (
                              <>
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span className="text-green-700">Sí</span>
                              </>
                            ) : (
                              <span className="text-muted-foreground">No</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Code */}
                  <div className="mt-8 pt-6 border-t">
                    <p className="text-center text-sm text-muted-foreground mb-2">
                      Código del certificado
                    </p>
                    <p className="text-center font-mono text-lg tracking-wider">
                      {data.certificate?.code}
                    </p>
                  </div>
                </div>

                {/* Identity Verification Badge */}
                {data.certificate?.identityVerified && (
                  <div className="bg-blue-50 border-t border-blue-200 p-4">
                    <div className="flex items-center justify-center gap-2 text-blue-800">
                      <Shield className="h-5 w-5" />
                      <span className="font-medium">
                        Identidad del titular verificada
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 text-center text-sm text-muted-foreground">
          <p>
            Los certificados de ARCUS Learning pueden ser verificados en
            cualquier momento usando el código único impreso en el documento.
          </p>
        </div>
      </main>
    </div>
  );
}
